import { ApplicationStatusEnum } from '@/app/common/general-types/briefs.dto';
import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, ElementRef, inject, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { form, minLength, required } from '@angular/forms/signals';
import { FormField } from '@angular/forms/signals';
import { Subscription } from 'rxjs';
import { BriefApplicationService } from '@/app/services/brief-application/brief-application.service';
import { ImageService } from '@/app/services/image/image.service';
import { ToastNotificationService } from '../toast-notification/toast-notification.service';

export interface BroadcastRecipient {
  id: string;
  creator: {
    firstName: string;
    lastName: string;
    photoURL: unknown;
    photoSrc: string;
  };
}

@Component({
  selector: 'atom-broadcast-message',
  imports: [FormField],
  templateUrl: './broadcast-message.html',
  styleUrl: './broadcast-message.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BroadcastMessage implements OnInit, OnDestroy {

  @ViewChild('broadcastMessageDialog', { static: false }) broadcastMessageDialog?: ElementRef<HTMLDialogElement>;
  @ViewChild('broadcastMessageButton', { static: false }) broadcastMessageButton?: ElementRef<HTMLButtonElement>;

  @Input() briefId!: string;
  /** Classes for the trigger button, so a host can match its own toolbar. */
  @Input() triggerClass = 'inline-flex items-center gap-x-1.5 btn-shout btn-shout-xs btn-shout-primary';

  targetStatusOptions: { value: 'hired' | 'started' | 'posting' | 'completed'; label: string }[] = [
    { value: 'hired', label: 'Hired' },
    { value: 'started', label: 'Started' },
    { value: 'posting', label: 'Posting' },
    { value: 'completed', label: 'Completed' }
  ];

  selectedTargetStatus = signal<{ value: 'hired' | 'started' | 'posting' | 'completed'; label: string }>({ value: 'hired', label: 'Hired' });

  messageFormModel = signal({
    message: ''
  });
  messageForm = form(this.messageFormModel, (schemaPath) => {
    required(schemaPath.message, { message: 'Message is required' });
    minLength(schemaPath.message, 30, { message: 'Message must be at least 30 characters long' });
  });

  recipients = signal<BroadcastRecipient[] | undefined>(undefined);
  selectedApplicationIDs = signal<string[]>([]);
  private readonly selectedIdSet = computed(() => new Set(this.selectedApplicationIDs()));

  /** Mounts heavy list content after the dialog opens to avoid forced reflow on showModal. */
  isContentReady = signal(false);
  sendingMessage = signal<boolean>(false);

  private subscriptionList = new Subscription();
  private openFrame: number | null = null;

  /** Services */
  private briefApplicationService = inject(BriefApplicationService);
  private imageService = inject(ImageService);
  private toastNotificationService = inject(ToastNotificationService);
  /** Services - END */

  ngOnInit(): void {
    this.loadBroadcastRecipients(this.selectedTargetStatus());
  }

  onDialogToggle(event: Event): void {
    const dialog = event.target as HTMLDialogElement;
    if (dialog.open) {
      this.openFrame = requestAnimationFrame(() => {
        this.isContentReady.set(true);
        this.openFrame = null;
      });
    } else {
      if (this.openFrame != null) {
        cancelAnimationFrame(this.openFrame);
        this.openFrame = null;
      }
      this.isContentReady.set(false);
    }
  }

  selectTargetStatus(status: { value: 'hired' | 'started' | 'posting' | 'completed'; label: string }): void {
    this.selectedTargetStatus.set(status);
    this.loadBroadcastRecipients(status);
  }

  loadBroadcastRecipients(status: { value: 'hired' | 'started' | 'posting' | 'completed'; label: string }): void {
    if (!this.briefId) {
      this.recipients.set([]);
      this.selectedApplicationIDs.set([]);
      return;
    }
    this.recipients.set(undefined);
    const selectedStatuses = this.getBroadcastStatusArray(status);

    this.subscriptionList.add(
      this.briefApplicationService.getBroadCastApplicants(this.briefId, selectedStatuses).subscribe({
        next: (recipients: Array<{ id: string; creator: { firstName: string; lastName: string; photoURL: unknown } }>) => {
          const mapped: BroadcastRecipient[] = recipients.map((r) => ({
            ...r,
            creator: {
              ...r.creator,
              photoSrc: this.imageService.getImageSource(r.creator.photoURL, 'gallery'),
            },
          }));
          this.recipients.set(mapped);
          this.selectedApplicationIDs.set(mapped.map((r) => r.id));
        },
        error: (error: unknown) => {
          console.error('Error loading broadcast recipients: ', error);
          this.recipients.set([]);
          this.selectedApplicationIDs.set([]);
        }
      })
    );
  }

  private getBroadcastStatusArray(status: { value: 'hired' | 'started' | 'posting' | 'completed'; label: string }): ApplicationStatusEnum[] {
    switch (status.value) {
      case 'hired':
        return [ApplicationStatusEnum.Hired];
      case 'started':
        return [ApplicationStatusEnum.Started];
      case 'posting':
        return [ApplicationStatusEnum.ReadyToPost];
      case 'completed':
        return [ApplicationStatusEnum.Completed, ApplicationStatusEnum.Posted, ApplicationStatusEnum.Paid];
      default:
        return [ApplicationStatusEnum.Hired];
    }
  }

  isRecipientSelected(recipient: { id: string }): boolean {
    return this.selectedIdSet().has(recipient.id);
  }

  toggleRecipientSelection(recipientId: string): void {
    const selected = this.selectedApplicationIDs();
    if (selected.includes(recipientId)) {
      this.selectedApplicationIDs.set(selected.filter((id) => id !== recipientId));
    } else {
      this.selectedApplicationIDs.set([...selected, recipientId]);
    }
  }

  toggleAllRecipientsSelection(): void {
    this.selectedApplicationIDs.set((this.recipients() ?? []).map((r) => r.id));
  }

  unToggleAllRecipientsSelection(): void {
    this.selectedApplicationIDs.set([]);
  }

  sendMessage(): void {
    this.sendingMessage.set(true);

    const payload: BroadcastStreamMessageDto = {
      message: this.messageFormModel().message,
      applicationStatusArray: [this.getBroadcastStatusArray(this.selectedTargetStatus())[0]],
      applicationIds: this.selectedApplicationIDs()
    };

    this.subscriptionList.add(
      this.briefApplicationService.broadcastStreamMessageByStatus(this.briefId, payload).subscribe({
        next: () => {
          this.sendingMessage.set(false);
          this.toastNotificationService.alert({
            title: 'Message Sent!',
            message: 'The message has been sent to the selected creators.',
            type: 'success',
            icon: 'success'
          });
          this.closeDrawer();
        },
        error: (error: unknown) => {
          this.sendingMessage.set(false);
          console.error('Error sending broadcast message: ', error);
        }
      })
    );
  }

  closeDrawer(): void {
    this.broadcastMessageDialog?.nativeElement?.close();
  }

  ngOnDestroy(): void {
    if (this.openFrame != null) {
      cancelAnimationFrame(this.openFrame);
    }
    this.subscriptionList.unsubscribe();
  }
}

export interface BroadcastStreamMessageDto {
  message: string;
  applicationStatusArray: ApplicationStatusEnum[];
  applicationIds?: string[];
}
